import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { resolveConsent, saveConsent, CONSENT_CHANGED_EVENT } from '~/shared/consent/consent';

const DEFAULT_CONSENT = { analytics: true, ads: true, optout: false, gpc: false };

export const ConsentContext = createContext({
  consent: DEFAULT_CONSENT,
  isPanelOpen: false,
  openPanel: () => {},
  closePanel: () => {},
  save: () => {},
  acceptAll: () => {},
  rejectAll: () => {},
});

export const ConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(DEFAULT_CONSENT);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  useEffect(() => {
    const resolved = resolveConsent();
    window.CGR_CONSENT = window.CGR_CONSENT || resolved;
    setConsent(window.CGR_CONSENT);

    const onChanged = (event) => setConsent(event.detail);
    window.addEventListener(CONSENT_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(CONSENT_CHANGED_EVENT, onChanged);
  }, []);

  const openPanel = useCallback(() => setIsPanelOpen(true), []);
  const closePanel = useCallback(() => setIsPanelOpen(false), []);

  const persistAndMaybeReload = useCallback((next, previous, { reload }) => {
    const turnedSomethingOff = (previous.analytics && !next.analytics) || (previous.ads && !next.ads);
    setIsPanelOpen(false);
    if (reload && turnedSomethingOff) {
      window.location.reload();
    }
  }, []);

  const save = useCallback(
    (partial) => {
      const previous = consent;
      const next = saveConsent(partial);
      setConsent(next);
      persistAndMaybeReload(next, previous, { reload: true });
    },
    [consent, persistAndMaybeReload]
  );

  const acceptAll = useCallback(() => {
    const previous = consent;
    const next = saveConsent({ analytics: true, ads: true, optout: false });
    setConsent(next);
    persistAndMaybeReload(next, previous, { reload: false });
  }, [consent, persistAndMaybeReload]);

  const rejectAll = useCallback(() => {
    const previous = consent;
    const next = saveConsent({ analytics: false, ads: false, optout: true });
    setConsent(next);
    persistAndMaybeReload(next, previous, { reload: true });
  }, [consent, persistAndMaybeReload]);

  const value = useMemo(
    () => ({ consent, isPanelOpen, openPanel, closePanel, save, acceptAll, rejectAll }),
    [consent, isPanelOpen, openPanel, closePanel, save, acceptAll, rejectAll]
  );

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
};

export const useConsent = () => useContext(ConsentContext);
